import { useState, useEffect } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { RangeToggle } from "@/components/common/RangeToggle";
import { KpiCards } from "@/components/analytics/KpiCards";
import { FunnelChart } from "@/components/analytics/FunnelChart";
import { DailyCharts } from "@/components/analytics/DailyCharts";
import { Skeleton } from "@/components/ui/skeleton";

interface KpiSummary {
  ordersCreated: number;
  revenue: number;
  conversionRate: number;
  avgOrderToQuote: number | null;
  avgQuoteToPaid: number | null;
  avgPickupToDelivered: number | null;
}

interface FunnelStep {
  name: string;
  count: number;
  percentage: number;
}

interface DailyMetric {
  date: string;
  count: number;
  revenue?: number;
}

export default function AnalyticsPage() {
  const [range, setRange] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<KpiSummary | null>(null);
  const [funnel, setFunnel] = useState<FunnelStep[]>([]);
  const [ordersPerDay, setOrdersPerDay] = useState<DailyMetric[]>([]);
  const [revenuePerDay, setRevenuePerDay] = useState<DailyMetric[]>([]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [summaryRes, funnelRes, dailyRes] = await Promise.all([
          fetch(`/api/admin/analytics/summary?range=${range}`),
          fetch(`/api/admin/analytics/funnel?range=${range}`),
          fetch(`/api/admin/analytics/daily?range=${range}`),
        ]);

        if (summaryRes.ok) {
          const data = await summaryRes.json();
          setSummary(data.summary);
        }

        if (funnelRes.ok) {
          const data = await funnelRes.json();
          setFunnel(data.funnel);
        }

        if (dailyRes.ok) {
          const data = await dailyRes.json();
          setOrdersPerDay(data.ordersPerDay);
          setRevenuePerDay(data.revenuePerDay);
        }
      } catch (error) {
        console.error("Failed to fetch analytics:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [range]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <PageHeader
          title="Analytics"
          subtitle="Business performance metrics and insights"
        />
        <RangeToggle value={range} onChange={setRange} />
      </div>

      {loading ? (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-[120px]" />
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Skeleton className="h-[350px]" />
            <Skeleton className="h-[350px]" />
          </div>
          <Skeleton className="h-[350px]" />
        </div>
      ) : (
        <>
          {summary && <KpiCards data={summary} />}
          <DailyCharts
            ordersPerDay={ordersPerDay}
            revenuePerDay={revenuePerDay}
          />
          {funnel.length > 0 && <FunnelChart data={funnel} />}
        </>
      )}
    </div>
  );
}
